// Monospace identifier pill with a copy-to-clipboard button. The value is
// rendered in a `font-mono` span; clicking the button writes to the system
// clipboard and announces success through an aria-live='polite' region so
// screen-reader users hear the confirmation. Used for policy numbers,
// reference numbers, and other compact identifiers across tracking views.
// The `hideValue` prop renders only the button + live region, for surfaces
// (e.g. a native select) that already display the value themselves.

import { useState } from "react";
import { Copy } from "lucide-react";

export interface CopyableIdentifierProps {
  value: string;
  /** Optional human label used in the live-region announcement and the
   *  button's aria-label. Without it, the announcement falls back to
   *  the generic "Identifier copied". */
  label?: string;
  /** Render only the copy button + live region (no visible value) — for
   *  surfaces that already display the value, e.g. a select. */
  hideValue?: boolean;
}

export default function CopyableIdentifier({
  value,
  label,
  hideValue,
}: CopyableIdentifierProps) {
  const [message, setMessage] = useState("");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(label ? `${label} copied` : "Identifier copied");
    } catch {
      setMessage("Copy failed");
    }
  };

  const accessibleName = `Copy ${label ?? value}`;

  return (
    <span className="inline-flex items-center gap-1.5">
      {!hideValue && (
        <span className="font-mono text-body-sm text-slate-900 dark:text-slate-100">
          {value}
        </span>
      )}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={accessibleName}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:text-brand-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-brand-400"
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        data-testid="copyable-identifier-live"
        aria-live="polite"
        className="sr-only"
      >
        {message}
      </span>
    </span>
  );
}
